import { useCallback, useEffect, useRef, useState } from 'react';

import { QrCodeView } from './QrCodeView';
import { apiClient } from '../services/apiClient';
import { authService } from '../services/authService';
import { qrLoginService, type QrLoginSession } from '../services/qrLoginService';

type QrLoginPanelProps = {
  onSignedIn?: () => void;
};

export function QrLoginPanel({ onSignedIn }: QrLoginPanelProps) {
  const [session, setSession] = useState<QrLoginSession | null>(null);
  const [remainingSeconds, setRemainingSeconds] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const pollTimerRef = useRef<number | null>(null);
  const countdownTimerRef = useRef<number | null>(null);

  const clearTimers = useCallback(() => {
    if (pollTimerRef.current != null) {
      window.clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
    if (countdownTimerRef.current != null) {
      window.clearInterval(countdownTimerRef.current);
      countdownTimerRef.current = null;
    }
  }, []);

  const bootstrap = useCallback(async () => {
    clearTimers();
    setLoading(true);
    setError(null);
    setConfirming(false);
    try {
      const next = await qrLoginService.createSession();
      setSession(next);
      setRemainingSeconds(next.remainingSeconds);
    } catch (bootstrapError) {
      setSession(null);
      setError(bootstrapError instanceof Error ? bootstrapError.message : '创建扫码登录失败');
    } finally {
      setLoading(false);
    }
  }, [clearTimers]);

  useEffect(() => {
    void bootstrap();
    return clearTimers;
  }, [bootstrap, clearTimers]);

  useEffect(() => {
    if (!session || remainingSeconds <= 0) {
      return undefined;
    }
    countdownTimerRef.current = window.setInterval(() => {
      setRemainingSeconds((current) => Math.max(0, current - 1));
    }, 1000);
    return () => {
      if (countdownTimerRef.current != null) {
        window.clearInterval(countdownTimerRef.current);
        countdownTimerRef.current = null;
      }
    };
  }, [remainingSeconds, session]);

  useEffect(() => {
    if (!session?.sessionId || loading || error) {
      return undefined;
    }
    const poll = async () => {
      try {
        const snapshot = await qrLoginService.poll(session.sessionId);
        if (snapshot.status === 'confirmed') {
          clearTimers();
          setConfirming(true);
          await apiClient.storeAuthResult(snapshot);
          try {
            await authService.fetchSession();
          } catch {
            // session already stored
          }
          onSignedIn?.();
          return;
        }
        if (snapshot.status === 'expired') {
          clearTimers();
          setError('二维码已过期，请刷新后重试');
          return;
        }
        if (snapshot.status === 'pending' && typeof snapshot.remainingSeconds === 'number') {
          setRemainingSeconds(snapshot.remainingSeconds);
        }
      } catch (pollError) {
        clearTimers();
        setError(pollError instanceof Error ? pollError.message : '查询扫码登录状态失败');
      }
    };

    void poll();
    pollTimerRef.current = window.setInterval(() => {
      void poll();
    }, session.pollIntervalMs || 1500);

    return clearTimers;
  }, [clearTimers, error, loading, onSignedIn, session]);

  useEffect(() => {
    if (remainingSeconds === 0 && session && !confirming && !loading && !error) {
      setError('二维码已过期，请刷新后重试');
      clearTimers();
    }
  }, [clearTimers, confirming, error, loading, remainingSeconds, session]);

  return (
    <div className="rounded-2xl border border-border bg-[#FAFBFD] p-4">
      <div className="text-sm font-semibold text-ink">微信小程序扫码登录</div>
      <p className="mt-1 text-xs text-muted">
        打开颗粒日记小程序 → 我 → 账户 → 扫一扫，扫描下方二维码即可登录。
      </p>

      <div className="mt-4 flex flex-col items-center gap-3">
        {loading ? (
          <div className="flex h-[240px] w-[240px] items-center justify-center rounded-2xl border border-dashed border-border bg-white text-sm text-muted">
            正在生成二维码…
          </div>
        ) : session ? (
          <div className="flex h-[240px] w-[240px] items-center justify-center rounded-2xl border border-border bg-white p-3">
            <QrCodeView value={session.qrPayload} size={216} label="小程序扫码登录二维码" />
          </div>
        ) : (
          <div className="flex h-[240px] w-[240px] items-center justify-center rounded-2xl border border-dashed border-border bg-white text-sm text-danger">
            二维码不可用
          </div>
        )}

        {confirming ? (
          <p className="text-sm font-medium text-success">扫码成功，正在进入…</p>
        ) : (
          <p className="text-xs text-muted">
            {remainingSeconds > 0 ? `${remainingSeconds}s 后过期` : '二维码已过期'}
          </p>
        )}

        {error ? <p className="text-xs text-danger">{error}</p> : null}

        <button
          type="button"
          onClick={() => { void bootstrap(); }}
          className="rounded-full border border-border px-4 py-2 text-xs font-semibold text-primary"
        >
          刷新二维码
        </button>
      </div>
    </div>
  );
}
